import React, { useState, useEffect } from 'react';
import { LifeBuoy, Send, MessageSquare, CheckCircle, Clock, AlertCircle, X, Plus, Loader2, Info, CreditCard, Wrench, Lightbulb, Bug, User } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { projectId } from '../utils/supabase/info';

import { bearerAuthHeader } from '../utils/apiHeaders';
interface SupportTicket {
  id: string;
  firmId: string;
  firmName: string;
  requestType: 'billing' | 'technical' | 'feature' | 'bug' | 'account';
  summary: string;
  description: string;
  status: string;
  priority: string;
  impact?: string;
  urgency?: string;
  createdBy: string;
  createdByName: string;
  createdByEmail: string;
  createdAt: string;
  updatedAt: string;
  jiraUrl: string;
  lastResponse?: string;
  lastResponseAt?: string;
  lastResponseBy?: string;
}

interface SupportComment {
  id: string;
  ticketId: string;
  author: string;
  authorEmail: string;
  body: string;
  createdAt: string;
  isInternal: boolean;
}

interface SupportDialogProps {
  open: boolean;
  onClose: () => void;
  firmId: string;
  firmName: string;
  userId: string;
  userName: string;
  userEmail: string;
}

export function SupportDialog({
  open,
  onClose,
  firmId,
  firmName,
  userId,
  userName,
  userEmail,
}: SupportDialogProps) {
  const [activeTab, setActiveTab] = useState<'submit' | 'tickets'>('submit');
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [ticketComments, setTicketComments] = useState<SupportComment[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // New ticket form state
  const [requestType, setRequestType] = useState<string>('');
  const [summary, setSummary] = useState('');
  const [description, setDescription] = useState('');
  const [impact, setImpact] = useState('Medium');
  const [urgency, setUrgency] = useState('Medium');
  
  // Comment state
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);

  // Filter state
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'resolved'>('all');
  
  // Diagnostic state
  const [showDiagnostic, setShowDiagnostic] = useState(false);
  const [diagnosticResult, setDiagnosticResult] = useState<any>(null);
  const [runningDiagnostic, setRunningDiagnostic] = useState(false);

  useEffect(() => {
    if (open && firmId) {
      fetchTickets();
    }
  }, [open, firmId]);

  const fetchTickets = async () => {
    if (!firmId) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/support/tickets/${firmId}`,
        {
          headers: {
            ...bearerAuthHeader(),
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setTickets(data.tickets || []);
      } else {
        console.error('Failed to fetch tickets');
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketDetails = async (ticketId: string) => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/support/tickets/${firmId}/${ticketId}`,
        {
          headers: {
            ...bearerAuthHeader(),
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSelectedTicket(data.ticket);
        setTicketComments(data.comments || []);
      } else {
        console.error('Failed to fetch ticket details');
      }
    } catch (error) {
      console.error('Error fetching ticket details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitTicket = async () => {
    if (!requestType || !summary.trim() || !description.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/support/tickets`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...bearerAuthHeader(),
          },
          body: JSON.stringify({
            firmId,
            firmName,
            requestType,
            summary,
            description,
            userId,
            userName,
            userEmail,
            impact,
            urgency,
            priority: 'Medium', // Default priority - support team will adjust based on impact/urgency
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('[SUPPORT] Ticket created successfully:', data.ticket);
        
        // Reset form
        setRequestType('');
        setSummary('');
        setDescription('');
        setImpact('Medium');
        setUrgency('Medium');
        
        // Refresh tickets
        await fetchTickets();
        
        // Show success message with mode indicator
        const mode = data.ticket.id.startsWith('KV-') ? '(Development Mode)' : '';
        alert(`Ticket ${data.ticket.id} created. ${mode}\n\nOur support team will review and prioritize your request, then respond within 4 hours.`);
        setActiveTab('tickets');
      } else {
        const error = await response.json();
        alert(`Failed to create ticket: ${error.details || error.error}`);
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      alert('Failed to create ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddComment = async () => {
    if (!selectedTicket || !newComment.trim()) return;

    setAddingComment(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/support/tickets/${firmId}/${selectedTicket.id}/comment`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...bearerAuthHeader(),
          },
          body: JSON.stringify({
            comment: newComment,
            userName,
            userEmail,
          }),
        }
      );

      if (response.ok) {
        setNewComment('');
        // Refresh ticket details
        await fetchTicketDetails(selectedTicket.id);
      } else {
        alert('Failed to add comment');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment. Please try again.');
    } finally {
      setAddingComment(false);
    }
  };

  const runDiagnostic = async () => {
    setRunningDiagnostic(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/support/jira-diagnostic`,
        {
          method: 'GET',
          headers: {
            ...bearerAuthHeader(),
          },
        }
      );

      const data = await response.json();
      setDiagnosticResult(data);
      console.log('[JIRA DIAGNOSTIC] Results:', data);
    } catch (error) {
      console.error('[JIRA DIAGNOSTIC] Error:', error);
      setDiagnosticResult({ error: 'Failed to run diagnostic' });
    } finally {
      setRunningDiagnostic(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Open':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Open</Badge>;
      case 'In Progress':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">In Progress</Badge>;
      case 'Waiting for Customer':
        return <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">Waiting</Badge>;
      case 'Resolved':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Resolved</Badge>;
      case 'Closed':
        return <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">Closed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'Highest':
      case 'High':
        return <Badge variant="destructive">{priority}</Badge>;
      case 'Medium':
        return <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">{priority}</Badge>;
      case 'Low':
      case 'Lowest':
        return <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">{priority}</Badge>;
      default:
        return <Badge variant="outline">{priority}</Badge>;
    }
  };

  const getRequestTypeIcon = (type: string) => {
    switch (type) {
      case 'billing':
        return <CreditCard className="size-5 text-muted-foreground" />;
      case 'technical':
        return <Wrench className="size-5 text-muted-foreground" />;
      case 'feature':
        return <Lightbulb className="size-5 text-muted-foreground" />;
      case 'bug':
        return <Bug className="size-5 text-muted-foreground" />;
      case 'account':
        return <User className="size-5 text-muted-foreground" />;
      default:
        return <MessageSquare className="size-5 text-muted-foreground" />;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString();
  };

  const filteredTickets = tickets.filter(ticket => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'open') return ticket.status === 'Open' || ticket.status === 'In Progress' || ticket.status === 'Waiting for Customer';
    if (statusFilter === 'resolved') return ticket.status === 'Resolved' || ticket.status === 'Closed';
    return true;
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col" aria-describedby="support-dialog-description">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LifeBuoy className="h-5 w-5 text-blue-600" />
            Platform Support
          </DialogTitle>
        </DialogHeader>
        <p id="support-dialog-description" className="sr-only">
          Submit support tickets and view your existing tickets
        </p>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="w-full flex flex-col flex-1 min-h-0">
          <TabsList className="grid w-full grid-cols-2 flex-shrink-0">
            <TabsTrigger value="submit" className="gap-2">
              <Plus className="h-4 w-4" />
              Submit Ticket
            </TabsTrigger>
            <TabsTrigger value="tickets" className="gap-2">
              <MessageSquare className="h-4 w-4" />
              My Tickets {tickets.length > 0 && `(${tickets.length})`}
            </TabsTrigger>
          </TabsList>

          {/* Submit Ticket Tab */}
          <TabsContent value="submit" className="space-y-4 overflow-y-auto flex-1 pr-2">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="request-type">Request Type *</Label>
                <Select value={requestType} onValueChange={setRequestType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type of request" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="billing">Billing Issue</SelectItem>
                    <SelectItem value="technical">Technical Support</SelectItem>
                    <SelectItem value="feature">Feature Request</SelectItem>
                    <SelectItem value="bug">Bug Report</SelectItem>
                    <SelectItem value="account">Account Help</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="summary">Subject *</Label>
                <Input
                  id="summary"
                  placeholder="Brief description of your issue"
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  maxLength={100}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  placeholder="Please provide detailed information about your request..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={8}
                  className="resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor="impact">Impact</Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3.5 w-3.5 text-gray-400 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs">How many people are affected by this issue?</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <Select value={impact} onValueChange={setImpact}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="High">High - Multiple users/departments affected</SelectItem>
                      <SelectItem value="Medium">Medium - Few users affected</SelectItem>
                      <SelectItem value="Low">Low - Single user affected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor="urgency">Urgency</Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3.5 w-3.5 text-gray-400 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs">How time-sensitive is this issue?</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <Select value={urgency} onValueChange={setUrgency}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="High">High - Work is blocked</SelectItem>
                      <SelectItem value="Medium">Medium - Work is impacted</SelectItem>
                      <SelectItem value="Low">Low - Can work around it</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitTicket}
                  disabled={submitting || !requestType || !summary.trim() || !description.trim()}
                  className="gap-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Submit Ticket
                    </>
                  )}
                </Button>
              </div>

              {/* Jira Diagnostic Section */}
              <div className="mt-6 pt-6 border-t">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDiagnostic(!showDiagnostic)}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  <Wrench className="size-3.5" /> Jira Integration Diagnostic {showDiagnostic ? '▼' : '▶'}
                </Button>
                
                {showDiagnostic && (
                  <div className="mt-3 space-y-3">
                    <Button
                      onClick={runDiagnostic}
                      disabled={runningDiagnostic}
                      size="sm"
                      variant="outline"
                    >
                      {runningDiagnostic ? 'Running...' : 'Test Jira Connection'}
                    </Button>
                    
                    {diagnosticResult && (
                      <div className="text-xs space-y-2">
                        <div className="p-3 bg-gray-50 rounded border space-y-1">
                          <div><strong>Configured:</strong> {diagnosticResult.configured ? 'Yes' : 'No'}</div>
                          <div><strong>Has Email:</strong> {diagnosticResult.hasEmail ? 'Yes' : 'No'}</div>
                          <div><strong>Has Token:</strong> {diagnosticResult.hasToken ? 'Yes' : 'No'}</div>
                          <div><strong>Project Key:</strong> {diagnosticResult.projectKey}</div>
                        </div>
                        
                        {diagnosticResult.projectTest && (
                          <div className="p-3 bg-gray-50 rounded border space-y-1">
                            <div><strong>Project Test:</strong></div>
                            <div>Status: {diagnosticResult.projectTest.status}</div>
                            <div>OK: {diagnosticResult.projectTest.ok ? 'Yes' : 'No'}</div>
                            {diagnosticResult.projectTest.name && <div>Name: {diagnosticResult.projectTest.name}</div>}
                            {diagnosticResult.projectTest.error && (
                              <div className="text-red-600 mt-1 break-words">
                                Error: {JSON.stringify(diagnosticResult.projectTest.error).substring(0, 200)}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {diagnosticResult.issueTypesTest && (
                          <div className="p-3 bg-gray-50 rounded border space-y-1">
                            <div><strong>Issue Types Test:</strong></div>
                            <div>Status: {diagnosticResult.issueTypesTest.status}</div>
                            <div>OK: {diagnosticResult.issueTypesTest.ok ? 'Yes' : 'No'}</div>
                            {diagnosticResult.issueTypesTest.availableTypes && (
                              <div className="mt-1">
                                <div className="font-semibold">Available Types:</div>
                                <ul className="list-disc list-inside ml-2">
                                  {diagnosticResult.issueTypesTest.availableTypes.map((type: string) => (
                                    <li key={type}>{type}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {diagnosticResult.issueTypesTest.error && (
                              <div className="text-red-600 mt-1 break-words">
                                Error: {JSON.stringify(diagnosticResult.issueTypesTest.error).substring(0, 200)}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {diagnosticResult.createMetaTest && (
                          <div className="p-3 bg-green-50 rounded border space-y-1">
                            <div><strong>Create Meta Test (Detailed):</strong></div>
                            <div>Status: {diagnosticResult.createMetaTest.status}</div>
                            <div>OK: {diagnosticResult.createMetaTest.ok ? 'Yes' : 'No'}</div>
                            {diagnosticResult.createMetaTest.projectName && (
                              <div>Project: {diagnosticResult.createMetaTest.projectName}</div>
                            )}
                            {diagnosticResult.createMetaTest.availableIssueTypes && diagnosticResult.createMetaTest.availableIssueTypes.length > 0 && (
                              <div className="mt-1">
                                <div className="font-semibold">Available Issue Types:</div>
                                <ul className="list-disc list-inside ml-2">
                                  {diagnosticResult.createMetaTest.availableIssueTypes.map((it: any) => (
                                    <li key={it.id}>
                                      <strong>{it.name}</strong> (ID: {it.id})
                                      {it.description && <div className="text-xs ml-4">{it.description}</div>}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {diagnosticResult.createMetaTest.impactField && (
                              <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                                <div className="font-semibold">Impact Field (customfield_10004):</div>
                                <div className="text-xs">Required: {diagnosticResult.createMetaTest.impactField.required ? 'Yes' : 'No'}</div>
                                {diagnosticResult.createMetaTest.impactField.allowedValues && diagnosticResult.createMetaTest.impactField.allowedValues.length > 0 && (
                                  <div className="mt-1">
                                    <div className="text-xs font-semibold">Allowed Values:</div>
                                    <ul className="list-disc list-inside ml-2 text-xs">
                                      {diagnosticResult.createMetaTest.impactField.allowedValues.map((v: any) => (
                                        <li key={v.id}>
                                          <strong>{v.value}</strong> (ID: {v.id})
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            )}
                            {diagnosticResult.createMetaTest.urgencyField && (
                              <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                                <div className="font-semibold">Urgency Field (customfield_10081):</div>
                                <div className="text-xs">Required: {diagnosticResult.createMetaTest.urgencyField.required ? 'Yes' : 'No'}</div>
                                {diagnosticResult.createMetaTest.urgencyField.allowedValues && diagnosticResult.createMetaTest.urgencyField.allowedValues.length > 0 && (
                                  <div className="mt-1">
                                    <div className="text-xs font-semibold">Allowed Values:</div>
                                    <ul className="list-disc list-inside ml-2 text-xs">
                                      {diagnosticResult.createMetaTest.urgencyField.allowedValues.map((v: any) => (
                                        <li key={v.id}>
                                          <strong>{v.value}</strong> (ID: {v.id})
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            )}
                            {diagnosticResult.createMetaTest.error && (
                              <div className="text-red-600 mt-1 break-words">
                                Error: {JSON.stringify(diagnosticResult.createMetaTest.error).substring(0, 200)}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {diagnosticResult.jsmTest && (
                          <div className="p-3 bg-blue-50 rounded border space-y-1">
                            <div><strong>JSM (Service Desk) Test:</strong></div>
                            <div>Status: {diagnosticResult.jsmTest.status}</div>
                            <div>OK: {diagnosticResult.jsmTest.ok ? 'Yes' : 'No'}</div>
                            {diagnosticResult.jsmTest.serviceDesks && diagnosticResult.jsmTest.serviceDesks.length > 0 && (
                              <div className="mt-1">
                                <div className="font-semibold">Service Desks Found:</div>
                                <ul className="list-disc list-inside ml-2">
                                  {diagnosticResult.jsmTest.serviceDesks.map((sd: any) => (
                                    <li key={sd.id}>
                                      {sd.projectName} ({sd.projectKey}) - ID: {sd.id}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {diagnosticResult.jsmTest.error && (
                              <div className="text-red-600 mt-1 break-words">
                                Error: {JSON.stringify(diagnosticResult.jsmTest.error).substring(0, 200)}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {diagnosticResult.testError && (
                          <div className="p-3 bg-red-50 border border-red-200 rounded text-red-600">
                            <strong>Test Error:</strong> {diagnosticResult.testError}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* My Tickets Tab */}
          <TabsContent value="tickets" className="space-y-4 overflow-y-auto flex-1 pr-2">
            {selectedTicket ? (
              // Ticket Detail View
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedTicket(null)}
                      >
                        ← Back
                      </Button>
                    </div>
                    <h3 className="text-lg flex items-center gap-2">
                      {getRequestTypeIcon(selectedTicket.requestType)} {selectedTicket.summary}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span>{selectedTicket.id}</span>
                      <span>•</span>
                      <span>Created {formatDate(selectedTicket.createdAt)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {getStatusBadge(selectedTicket.status)}
                    {getPriorityBadge(selectedTicket.priority)}
                    {selectedTicket.impact && (
                      <Badge variant="outline" className="text-xs">
                        Impact: {selectedTicket.impact}
                      </Badge>
                    )}
                    {selectedTicket.urgency && (
                      <Badge variant="outline" className="text-xs">
                        Urgency: {selectedTicket.urgency}
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm whitespace-pre-wrap">{selectedTicket.description}</p>
                </div>

                {selectedTicket.lastResponse && (
                  <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                    <div className="flex items-start gap-2 mb-2">
                      <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                      <div>
                        <div className="text-sm text-blue-900">
                          Last response from {selectedTicket.lastResponseBy || 'PathKit Support'}
                        </div>
                        <div className="text-xs text-blue-700">
                          {selectedTicket.lastResponseAt && formatDate(selectedTicket.lastResponseAt)}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-blue-900 whitespace-pre-wrap pl-7">
                      {selectedTicket.lastResponse}
                    </p>
                  </div>
                )}

                <div className="space-y-3">
                  <h4 className="font-medium">Comments</h4>
                  <ScrollArea className="h-64 border rounded-lg p-4">
                    {ticketComments.length === 0 ? (
                      <div className="text-center text-gray-500 py-8">
                        No comments yet
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {ticketComments.map((comment) => (
                          <div
                            key={comment.id}
                            className={`p-3 rounded-lg ${
                              comment.isInternal
                                ? 'bg-blue-50 border border-blue-200'
                                : 'bg-gray-50'
                            }`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="text-sm">
                                <span className="font-medium">{comment.author}</span>
                                {comment.isInternal && (
                                  <Badge variant="outline" className="ml-2 text-xs bg-blue-100 text-blue-700 border-blue-300">
                                    PathKit Support
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs text-gray-500">
                                {formatDate(comment.createdAt)}
                              </div>
                            </div>
                            <p className="text-sm whitespace-pre-wrap">{comment.body}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>

                  {selectedTicket.status !== 'Resolved' && selectedTicket.status !== 'Closed' && (
                    <div className="space-y-2">
                      <Textarea
                        placeholder="Add a comment..."
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        rows={3}
                      />
                      <div className="flex justify-end">
                        <Button
                          onClick={handleAddComment}
                          disabled={addingComment || !newComment.trim()}
                          size="sm"
                          className="gap-2"
                        >
                          {addingComment ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send className="h-4 w-4" />
                              Add Comment
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t">
                  <a
                    href={selectedTicket.jiraUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline"
                  >
                    View in Jira →
                  </a>
                </div>
              </div>
            ) : (
              // Ticket List View
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button
                      variant={statusFilter === 'all' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setStatusFilter('all')}
                    >
                      All ({tickets.length})
                    </Button>
                    <Button
                      variant={statusFilter === 'open' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setStatusFilter('open')}
                    >
                      Open ({tickets.filter(t => ['Open', 'In Progress', 'Waiting for Customer'].includes(t.status)).length})
                    </Button>
                    <Button
                      variant={statusFilter === 'resolved' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setStatusFilter('resolved')}
                    >
                      Resolved ({tickets.filter(t => ['Resolved', 'Closed'].includes(t.status)).length})
                    </Button>
                  </div>
                </div>

                <ScrollArea className="h-96">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                    </div>
                  ) : filteredTickets.length === 0 ? (
                    <div className="text-center text-gray-500 py-12">
                      <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No tickets found</p>
                      <p className="text-sm mt-1">Submit a ticket to get help from our support team</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredTickets.map((ticket) => (
                        <button
                          key={ticket.id}
                          onClick={() => fetchTicketDetails(ticket.id)}
                          className="w-full text-left p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="flex items-start gap-2 flex-1 min-w-0">
                              <span className="text-xl flex-shrink-0">{getRequestTypeIcon(ticket.requestType)}</span>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium truncate">{ticket.summary}</h4>
                                <div className="text-sm text-gray-600 flex items-center gap-2 mt-1">
                                  <span className="truncate">{ticket.id}</span>
                                  <span>•</span>
                                  <span className="whitespace-nowrap">{formatDate(ticket.createdAt)}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 flex-wrap flex-shrink-0">
                              {getStatusBadge(ticket.status)}
                              {ticket.impact && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Badge variant="outline" className="text-xs cursor-help">
                                        I:{ticket.impact.charAt(0)}
                                      </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Impact: {ticket.impact}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                              {ticket.urgency && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Badge variant="outline" className="text-xs cursor-help">
                                        U:{ticket.urgency.charAt(0)}
                                      </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Urgency: {ticket.urgency}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                          </div>
                          {ticket.lastResponse && (
                            <div className="text-sm text-blue-600 mt-2 flex items-center gap-1">
                              <CheckCircle className="h-3 w-3" />
                              <span>Response from support {ticket.lastResponseAt && `• ${formatDate(ticket.lastResponseAt)}`}</span>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}